<template>
    <div :id="`common-image-upload-${imgUploadId}`">
        <div :class="['file-container', { 'border border-danger': hasError, 'd-none': fileOnly }]"
            :style="{ 'height': isThumbNail && (imgPreview || imgRaw) ? 'calc(68px + 135px)' : '68px' }">
            <div class=" drop-zone flex-column" @dragenter="dragEnter" @dragleave="dragLeave" @dragover.prevent
                @drop.stop.prevent="handleFile">
                <div class="d-flex align-items-center justify-content-center">
                    <div>
                        <img src="@/assets/images/upload-file.svg" alt="Upload">
                    </div>
                    <div>
                        <p v-if="!isDragging" class="ml-3 mt-0">Drag & drop your file here or
                            <span class="text-primary cursor-pointer" @click="openFileInput">
                                browse
                            </span>
                        </p>
                        <p v-else class="ml-3">Drop your file here</p>
                    </div>
                </div>
                <div v-if="isThumbNail && (imgPreview || imgRaw)" class="position-relative mt-3">
                    <div class="image-preview">
                        <img :src="imgPreview || imgRaw" alt="Preview" class="h-100" />
                        <img src="@/assets/images/remove-filled.svg" class="ml-3 position-absolute cursor-pointer"
                            @click="clearData" alt="Close">
                    </div>
                </div>
            </div>
            <input type="file" ref="fileInput" class="d-none" @change="handleFile" />
            <div class="upload-footer">
                <p>{{ recommendation }}</p>
                <p>Maximum file size: {{ maxFileSizeInMB }}mb</p>
            </div>
            <div v-if="!!warningMessage && showWarningMessage" class="align-self-start mt-1">
                <small class="text-danger">{{ warningMessage }}</small>
            </div>
        </div>
        <div v-if="!isThumbNail && (fileName || link)" class="file-preview"
            :class="{ 'border border-danger': !!warningMessage, 'd-none': fileOnly }">
            <div class="title">
                <img src="@/assets/images/icons/image-icon.svg" alt="Icon">
                <div class="sub-title">
                    <p class="file-name">{{ fileName ? fileName : link }}</p>
                </div>
            </div>

            <div v-if="fileName" class="d-flex align-items-center">
                <div class="file-size">{{ fileSize }} kb</div>
                <img src="@/assets/images/onboarding/closeIcon.svg" class="ml-3 cursor-pointer" @click="clearData"
                    alt="Close">
            </div>
        </div>
    <ImageCropperModal :source="imageSource" @onDeleteImage="clearData" @onCropImage="loadCroppedImage"
            :aspectRatio="aspectRatio" :cropperModalId="imgUploadId" />
    </div>
</template>

<script>
import ImageCropperModal from "@/components/common/ImageCropperModal";

export default {
    components: {
        ImageCropperModal
    },
    props: {
        link: {
            type: String,
            default: ""
        },
        extensions: {
            type: Array,
            default: () => []
        },
        hasError: {
            type: Boolean,
            default: false
        },
        maxFileSizeInMB: {
            type: Number,
            default: 2
        },
        recommendation: {
            type: String,
            default: ""
        },
        enableCropping: {
            type: Boolean,
            default: false
        },
        fileOnly: {
            type: Boolean,
            default: false
        },
        aspectRatio: {
            type: String,
            default: null
        },
        resetImageUploader: {
            type: Boolean,
            default: false
        },
        showWarningMessage: {
            type: Boolean,
            default: true
        },
        isThumbNail: {
            type: Boolean,
            default: false
        },
        imgRaw: {
            type: String,
            default: null
        },
        imgUploadId: {
            type: String,
            default: ""
        }
    },
    data() {
        return {
            isDragging: false,
            fileName: '',
            fileSize: '',
            warningMessage: '',
            imageSource: null,
            imgPreview: null
        };
    },
    methods: {
        clearData() {
            this.$refs.fileInput.value = null;
            this.warningMessage = '';
            this.fileName = '';
            this.fileSize = '';
            this.imgPreview = null;
            this.$emit("onClearImage");
        },
        dragEnter() {
            this.isDragging = true;
        },
        dragLeave() {
            this.isDragging = false;
        },
        async handleFile(event) {
            let files;

            if (event.type === "drop") {
                files = event.dataTransfer.files;
                this.isDragging = false;
            } else {
                files = event.target.files;
            }

            const imageSource = await this.processFile(files[0]);

            if (imageSource && this.enableCropping)
                this.$root.$emit("bv::show::modal", `common-image-cropper-modal-${this.imgUploadId}`);
        },
        async handleDrop(event) {
            event.preventDefault();
            this.isDragging = false;
            const files = event.dataTransfer.files;
            const imageSource = await this.processFile(files[0]);

            if (imageSource && this.enableCropping)
                this.$root.$emit("bv::show::modal", `common-image-cropper-modal-${this.imgUploadId}`);
        },
        async handleFileChange(event) {
            const files = event.target.files;
            const imageSource = await this.processFile(files[0]);

            if (imageSource && this.enableCropping)
                this.$root.$emit("bv::show::modal", "common-image-cropper-modal");
        },
        openFileInput() {
            this.$refs.fileInput.click();
        },
        bytesToKB(fileSizeInBytes) {
            return (fileSizeInBytes / 1024).toFixed(2);
        },
        truncate(value) {
            return value.length > 24 ? `${value.substr(0, 12)}...${value.substr(-12)}` : value;
        },
        async processFile(file) {
            this.clearData();

            const validFileSizeInBytes = this.maxFileSizeInMB * 1024 * 1024;
            const fileExtension = this.getFileExtension(file.name);

            if (!this.extensions.includes(fileExtension)) {
                this.warningMessage = `Invalid file format. Accepted formats: ${this.extensions.join(', ')}`;
                this.$emit("hasWarning", this.warningMessage);
                return;
            }

            if (file.size > validFileSizeInBytes) {
                this.warningMessage = `File size exceeds the limit. Maximum size: ${this.maxFileSizeInMB}MB`;
                this.$emit("hasWarning", this.warningMessage);
                return;
            }

            this.fileName = this.truncate(file.name);
            this.fileSize = this.bytesToKB(file.size);
            this.imageSource = await this.getImageSource(file);
            this.$emit("hasWarning", this.warningMessage);
            // this.$emit("onPreviewImage", this.imageSource);

            return this.imageSource;
        },
        getFileExtension(filename) {
            return filename.slice(filename.lastIndexOf('.')).toLowerCase();
        },
        getImageSource(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = (e) => {
                    return resolve(e.target.result);
                };
                reader.onerror = (error) => {
                    return reject(error);
                };
            });
        },
        loadCroppedImage(croppedImage) {
            this.$emit("onPreviewImage", croppedImage);
            this.imageSource = null;
            this.imgPreview = croppedImage;
        }
    },
    watch: {
        resetImageUploader(value) {
            if (value) this.clearData();
        }
    }
};
</script>
<style lang="scss" scoped>
div[id^="common-image-upload-"] {
    font-family: MainFontRegular, sans-serif;

    .image-preview {
        width: 135px;
        height: 135px;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        border-radius: 6px;

        &>img {
            &:first-child {
                object-fit: fill;
            }

            &:last-child {
                left: 108px;
                top: -5px;
                z-index: 9;
                background: #fff;
                border-radius: 50%;
            }
        }
    }

    .file-preview {
        border-radius: 8px;
        border: 1px solid #E9EBED;
        margin-top: 40px;
        min-height: 55px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 9px;
        word-wrap: break-word;

        .title {
            display: flex;

            img {
                margin-right: 8px;
            }

            .sub-title {
                display: flex;
                flex-direction: column;
                align-items: start;
                justify-content: center;
                max-width: 300px;

                p {
                    margin-bottom: 0px;
                }

                .file-name {
                    font-size: 13px;
                    color: #2F3941;
                }

                @media (max-width: 672px) {
                    max-width: 100px;
                }
            }
        }

        .file-size {
            color: #87929D;
            font-size: 12px;
        }
    }

    .upload-footer {
        margin-top: 7px;
        align-items: center;
        display: flex;
        justify-content: space-between;
        width: 100%;

        p {
            margin-top: 7px;
            margin-bottom: 0px;
            color: #87929D;
            font-size: 12px;
            font-style: normal;
            font-weight: 400;
            line-height: normal;
        }
    }

    .drop-zone button {
        margin-top: 10px;
        background-color: #53b6b2;
        color: #ffffff;
        border: none;
        padding: 8px 16px;
        border-radius: 4px;
    }

    .drop-zone button:hover {
        background-color: #3a8f8a;
    }

    .file-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        border-radius: 8px;
        min-height: 68px;
        border: 2px dashed #D8DCDE;
    }

    .drop-zone {
        width: 100%;
        height: 100%;
        text-align: center;
        cursor: pointer;
        display: flex;
        justify-content: center;
        padding: 14px;
    }

    .drop-zone p {
        margin: 0;
        font-size: 14px;
        color: #666;
    }

    .drop-zone p:last-child {
        margin-top: 10px;
    }

    .drop-zone.dragging {
        border-color: #53b6b2;
    }

    @keyframes slide {
        0% {
            background-position: 0 0;
        }

        100% {
            background-position: -100% 0;
        }
    }

}
</style>
